import { useEffect, useRef, useState } from 'react';
import type {
  ClipboardEvent as ColagemReact,
  CSSProperties,
  KeyboardEvent as TeclaReact,
  RefObject,
} from 'react';
import { comMarcas } from './marcas';

/*
 * ---------------------------------------------------------------------------
 * CÓDIGO PARADO — a escrita mudou de lugar
 * ---------------------------------------------------------------------------
 *
 * Este componente editava o texto **no próprio papel**. Ele deixou de ser usado
 * quando a tela do editor virou três colunas e a escrita passou para o editor de
 * texto (`documento/EditorDeTexto.tsx`, na coluna do meio): o papel virou **só
 * desenho**, e ninguém mais chega aqui com `editavel` ligado.
 *
 * Fica no repositório até a limpeza, que é a poda das props de edição do
 * `DocumentoImpresso` e deste arquivo: `aoEditarTexto`, `aoFocarTexto`,
 * `aoMoverCursor`, `focoPedido`, `aoNovoBloco`, `aoApagarBloco` e o `AcoesDoTexto`
 * que atravessa parágrafo, item de lista e título.
 *
 * O que se aprendeu aqui continua valendo para quem for mexer: `contentEditable` e
 * React disputam o mesmo DOM, e o conteúdo do bloco tem de ser escrito por fora —
 * e só quando o documento muda (ver os comentários abaixo, no corpo do componente).
 */

/**
 * A qual pedaço do documento a edição se refere.
 *
 * A identidade mudou junto com os blocos: antes o bloco editável era "o parágrafo N
desta seção" (uma posição numa lista de textos), e agora é **o bloco** — que é o
 * que permite ter lista, citação e parágrafo como coisas de tipos diferentes, e
 * mover um deles de lugar sem que o editor perca o fio de qual é qual.
 */
export type MudancaDeTexto =
  | { qual: 'BLOCO'; secaoId: string; blocoId: string }
  | { qual: 'ITEM'; secaoId: string; blocoId: string; indice: number }
  | { qual: 'TITULO'; secaoId: string };

interface Props {
  /** O texto em Markdown, do jeito que ele vive no documento. */
  valor: string;
  /** A que elemento o bloco corresponde (o papel usa `p`, `li`, `h2` e `span`). */
  como?: 'p' | 'span' | 'li' | 'h1' | 'h2' | 'h3';
  style?: CSSProperties;
  /** Sem isto o bloco é só leitura (a impressão não se edita). */
  editavel: boolean;
  aoGravar?: (novo: string) => void;
  /** O que se lê para quem usa leitor de tela. */
  rotulo?: string;
  className?: string;
  /** Marcas de dados que o desenho e os testes usam (`data-citacao-longa`, por exemplo). */
  dados?: Record<string, string>;
  /**
   * O bloco avisa que recebeu o foco (a barra de ferramentas age sobre ele) e
   * **entrega o próprio elemento**: é por ele que o editor devolve o cursor antes
   * de inserir uma citação, por exemplo.
   */
  aoFocar?: (no: HTMLElement | null) => void;
  /**
   * Onde o cursor está dentro do bloco, em letras — e em que bloco.
   *
   * Existe porque inserir no cursor exige **lembrar** dele: quem escolhe a
   * citação digita a página num campo, e o campo rouba o foco do texto — sem a
   * posição guardada, a citação cairia no começo do parágrafo ou no vazio.
   */
  aoMoverCursor?: (posicao: { inicio: number; fim: number }, no: HTMLElement) => void;
  /** `Enter`: o editor cria um parágrafo depois deste bloco (como um editor de textos). */
  aoNovoParagrafo?: () => void;
  /** `Backspace` num parágrafo vazio: o editor tira o parágrafo e volta o foco ao anterior. */
  aoApagarVazio?: () => void;
  /** O editor pediu o foco para este bloco (depois de criar ou apagar um parágrafo). */
  deveFocar?: 'inicio' | 'fim' | false;
}

/**
 * O texto do papel que se edita ali mesmo — o jeito do Typora.
 *
 * A ideia: **não existe caixa de texto separada do que vai ser impresso**. A pessoa
 * clica no parágrafo, no título ou na citação, no próprio papel, e escreve; as
 * marcas do Markdown (`**negrito**`, `*itálico*`) aparecem **aplicadas** enquanto
 * ela escreve, e não como asteriscos no meio da frase. Clicar em outro lugar grava:
 * o que está na tela vira Markdown de novo e entra no documento.
 *
 * A decisão que sustenta o resto: **o React não escreve dentro deste bloco.**
 *
 * `contentEditable` e React disputam o mesmo DOM: o navegador junta, divide e
 * substitui nós enquanto a pessoa digita, e quando o React tenta tirar dali o que
 * ele acha que pôs, a página cai com "Failed to execute 'removeChild' on 'Node'".
 * Foi o que aconteceu na primeira versão deste componente. Então o conteúdo é escrito
 * **por fora do React** (`marcasEmHtml` → `innerHTML`), e só quando o texto do
 * documento muda. Entre uma mudança e outra — isto é, durante a digitação —, o DOM é
 * do navegador e de mais ninguém.
 *
 * As outras três decisões, cada uma com uma armadilha atrás:
 *
 * - **A gravação é ao sair do bloco** (`blur`) e ao sair da tela: o que está no DOM
 *   volta a ser Markdown (`paraMarkdown`) e entra no documento.
 * - **Enter não quebra a linha**: parágrafo é bloco, e quem cria parágrafo é o botão
 *   do painel (ou o texto colado). Deixar o navegador quebrar a linha aqui criaria um
 *   `div`/`br` que o papel imprimiria como espaço — defeito silencioso.
 * - **`Ctrl+B` e `Ctrl+I`** põem negrito e itálico pelo próprio navegador, e colar
 *   entra como **texto puro**, sem a formatação de quem copiou.
 */
export function TextoEditavel({
  valor,
  como = 'p',
  style,
  editavel,
  aoGravar,
  rotulo,
  className,
  dados,
  aoFocar,
  aoMoverCursor,
  aoNovoParagrafo,
  aoApagarVazio,
  deveFocar = false,
}: Props) {
  const no = useRef<HTMLElement>(null);
  const [emEdicao, setEmEdicao] = useState(false);
  /** O que está escrito agora, para não se perder se o bloco sair da tela. */
  const rascunho = useRef<string | null>(null);

  /*
   * O conteúdo do bloco, escrito à mão: as marcas viram negrito e itálico de
   * verdade, e o texto sai escapado (o texto é de quem escreve, mas `<`, `>` e `&`
   * não têm nada que virar HTML aqui dentro).
   *
   * O efeito roda quando o **documento** muda — e, durante a digitação, não roda
   * nunca: é isso que mantém o cursor no lugar.
   */
  useEffect(() => {
    const atual = no.current;
    if (!atual || !editavel) return;
    atual.innerHTML = marcasEmHtml(valor);
  }, [valor, editavel]);

  /*
   * Trocar de aba (ou apagar o parágrafo) no meio da edição também grava: o bloco
   * sai da tela sem `blur`, e o que a pessoa escreveu não pode sumir em silêncio.
   */
  useEffect(() => {
    if (!emEdicao) return;
    return () => {
      const escrito = rascunho.current;
      if (escrito !== null && escrito !== valor) aoGravar?.(escrito);
    };
  }, [emEdicao, aoGravar, valor]);

  /*
   * O foco pedido pelo editor: depois de criar ou apagar um parágrafo, o cursor
   * precisa cair no bloco certo — senão a pessoa teria de clicar antes de continuar
   * escrevendo, e um editor de textos não faz isso.
   */
  useEffect(() => {
    if (!deveFocar) return;
    const atual = no.current;
    if (!atual) return;
    atual.focus();
    const intervalo = document.createRange();
    intervalo.selectNodeContents(atual);
    intervalo.collapse(deveFocar === 'inicio');
    const selecao = window.getSelection();
    selecao?.removeAllRanges();
    selecao?.addRange(intervalo);
  }, [deveFocar]);

  /*
   * O elemento muda de nome (`p`, `h2`...), e o TypeScript não consegue amarrar o
   * `ref` a um nome dinâmico: os dois apontam para o mesmo nó de HTML.
   */
  const Bloco = como as 'p';
  const referencia = no as RefObject<HTMLParagraphElement>;

  if (!editavel) {
    return (
      <Bloco style={style} className={className} {...dados}>
        {comMarcas(valor)}
      </Bloco>
    );
  }

  const gravar = () => {
    setEmEdicao(false);
    rascunho.current = null;
    const atual = no.current;
    if (!atual) return;
    const novo = paraMarkdown(atual);
    if (novo !== valor) aoGravar?.(novo);
  };

  const aoTeclar = (evento: TeclaReact<HTMLElement>) => {
    /*
     * `Enter` cria um **parágrafo novo**, como em qualquer editor de textos — e a
     * quebra de linha solta continua fora: parágrafo é bloco, e um `br`/`div` que o
     * navegador criasse aqui seria impresso como espaço, defeito silencioso.
     */
    if (evento.key === 'Enter') {
      evento.preventDefault();
      if (aoNovoParagrafo) aoNovoParagrafo();
      else no.current?.blur();
      return;
    }
    if (evento.key === 'Escape') {
      evento.preventDefault();
      no.current?.blur();
      return;
    }
    /*
     * `Backspace` num parágrafo **vazio** tira o parágrafo e volta o foco ao
     * anterior — o gesto que todo mundo espera de um editor. Com texto dentro, o
     * `Backspace` é do navegador (apaga a letra), e juntar dois parágrafos é
     * selecionar e apagar.
     */
    if (evento.key === 'Backspace' && aoApagarVazio && no.current?.textContent === '') {
      evento.preventDefault();
      aoApagarVazio();
      return;
    }
    if (!(evento.ctrlKey || evento.metaKey)) return;
    /* Negrito e itálico são do próprio navegador — é o que mantém o desfazer vivo. */
    if (evento.key.toLowerCase() === 'b') {
      evento.preventDefault();
      document.execCommand('bold');
    } else if (evento.key.toLowerCase() === 'i') {
      evento.preventDefault();
      document.execCommand('italic');
    }
  };

  const aoColar = (evento: ColagemReact<HTMLElement>) => {
    /*
     * Colar entra como **texto puro**: a formatação de quem copiou (do Word, de uma
     * página) traria `span` com cor e letra que o papel não tem como imprimir — e o
     * documento é Markdown, não HTML.
     */
    evento.preventDefault();
    const texto = evento.clipboardData
      .getData('text/plain')
      .replace(/\s+/g, ' ')
      .trim();
    if (texto) document.execCommand('insertText', false, texto);
  };

  const anotar = () => {
    const atual = no.current;
    if (!atual) return;
    rascunho.current = paraMarkdown(atual);
    avisarCursor();
  };

  /** Conta onde o cursor está e conta para o editor (ver `aoMoverCursor`). */
  const avisarCursor = () => {
    const atual = no.current;
    if (!atual || !aoMoverCursor) return;
    const posicao = deslocamentoDaSelecao(atual);
    if (posicao) aoMoverCursor(posicao, atual);
  };

  return (
    <Bloco
      ref={referencia}
      /*
       * Editar no próprio papel: o bloco é editável de verdade, e o conteúdo dele é
       * escrito pelo efeito acima — nunca por filhos de React (ver a explicação).
       */
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      role="textbox"
      aria-label={rotulo}
      tabIndex={0}
      data-texto-editavel=""
      onFocus={() => {
        setEmEdicao(true);
        aoFocar?.(no.current);
        avisarCursor();
      }}
      onInput={anotar}
      onBlur={gravar}
      onKeyDown={aoTeclar}
      /* A seta anda sem digitar nada: sem isto o cursor lembrado ficaria velho. */
      onKeyUp={avisarCursor}
      onMouseUp={avisarCursor}
      onPaste={aoColar}
      style={style}
      className={className}
      {...dados}
    />
  );
}

/** O Markdown do bloco em HTML pronto para o `contentEditable`. */
function marcasEmHtml(texto: string): string {
  const escapado = texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escapado.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>').replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
}

/**
 * Onde a seleção está dentro do bloco, medido em **letras** (e não em nós do DOM).
 *
 * Letra é a unidade que sobrevive à edição: o navegador divide e junta nós de texto
 * o tempo todo (uma marca de negrito, uma colagem), e uma posição guardada em nó
 * apontaria para um pedaço que já não existe. Contar caracteres é o que permite
 * voltar ao mesmo lugar depois — e é o que o editor precisa para inserir a citação
 * onde a pessoa estava escrevendo.
 */
export function deslocamentoDaSelecao(no: HTMLElement): { inicio: number; fim: number } | null {
  const selecao = window.getSelection();
  if (!selecao || selecao.rangeCount === 0) return null;
  const intervalo = selecao.getRangeAt(0);
  if (!no.contains(intervalo.startContainer) || !no.contains(intervalo.endContainer)) return null;
  return {
    inicio: letrasAte(no, intervalo.startContainer, intervalo.startOffset),
    fim: letrasAte(no, intervalo.endContainer, intervalo.endOffset),
  };
}

/** Quantas letras há do começo do bloco até este ponto. */
function letrasAte(no: HTMLElement, alvo: Node, deslocamento: number): number {
  const intervalo = document.createRange();
  intervalo.selectNodeContents(no);
  intervalo.setEnd(alvo, deslocamento);
  return intervalo.toString().length;
}

/** Põe a seleção de volta no lugar, em letras — o caminho de volta do `deslocamentoDaSelecao`. */
export function porSelecaoEm(no: HTMLElement, inicio: number, fim: number): void {
  const selecao = window.getSelection();
  if (!selecao) return;

  const inicioNo = pontoDeLetra(no, inicio);
  const fimNo = pontoDeLetra(no, Math.max(inicio, fim));
  const intervalo = document.createRange();
  intervalo.setStart(inicioNo.no, inicioNo.deslocamento);
  intervalo.setEnd(fimNo.no, fimNo.deslocamento);
  selecao.removeAllRanges();
  selecao.addRange(intervalo);
}

/** O nó de texto (e o quanto dele) onde a letra de número `letra` começa. */
function pontoDeLetra(no: HTMLElement, letra: number): { no: Node; deslocamento: number } {
  const caminhante = document.createTreeWalker(no, NodeFilter.SHOW_TEXT);
  let restante = letra;
  let ultimo: Node | null = null;

  while (caminhante.nextNode()) {
    const texto = caminhante.currentNode as Text;
    const tamanho = (texto.textContent ?? '').length;
    /*
     * O ponto é a **pontinha** entre duas letras: quando a conta chega ao fim de um
     * nó de texto, o lugar certo é logo depois dele — e é o próximo nó que recebe
     * isso, com deslocamento zero. Sem o `ultimo`, um cursor no fim do parágrafo
     * cairia no começo do último pedaço.
     */
    if (restante <= tamanho) return { no: texto, deslocamento: restante };
    restante -= tamanho;
    ultimo = texto;
  }

  if (ultimo) return { no: ultimo, deslocamento: (ultimo.textContent ?? '').length };
  return { no, deslocamento: 0 };
}

/**
 * O HTML do bloco de volta em Markdown.
 *
 * Só duas marcas sobrevivem — negrito e itálico —, porque são as duas que o papel
 * sabe imprimir e as duas que a leitura do texto colado entende. Qualquer outro
 * elemento que o navegador tenha criado (um `span` de colagem, um `div` de quebra)
 * entra como **texto**, e a quebra de linha vira espaço: no meio de um parágrafo,
 * linha nova é o mesmo que espaço.
 *
 * O acabamento (espaços repetidos e pontas) é **do último passo**, e não de cada
 * pedaço: se cada marca já viesse aparada, o espaço que a pessoa digitou antes de
 * ligar o negrito — que o navegador guarda **dentro** da marca, como espaço não
 * separável — desapareceria, e a frase sairia "meio.**Fim**". Foi o que aconteceu.
 */
export function paraMarkdown(no: Node): string {
  return percorrer(no).replace(/\s+/g, ' ').trim();
}

/** O passe interno: lê o DOM sem aparar nada (o acabamento é do `paraMarkdown`). */
function percorrer(no: Node): string {
  let saida = '';

  no.childNodes.forEach((filho) => {
    if (filho.nodeType === 3) {
      saida += filho.textContent ?? '';
      return;
    }
    if (filho.nodeType !== 1) return;

    const elemento = filho as HTMLElement;
    const nome = elemento.tagName.toLowerCase();
    if (nome === 'br') {
      saida += ' ';
      return;
    }

    const dentro = percorrer(elemento);
    /* O espaço que ficou dentro da marca é espaço do texto, e volta para fora dela. */
    const antes = /^\s/.test(dentro) ? ' ' : '';
    const depois = /\s$/.test(dentro) ? ' ' : '';
    if (nome === 'strong' || nome === 'b') {
      saida += dentro.trim() ? `${antes}**${dentro.trim()}**${depois}` : '';
      return;
    }
    if (nome === 'em' || nome === 'i') {
      saida += dentro.trim() ? `${antes}*${dentro.trim()}*${depois}` : '';
      return;
    }
    saida += dentro;
  });

  return saida;
}
